import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const slides = [
  {
    id: 1,
    image: 'https://cdn.shopify.com/s/files/1/0971/0187/3536/files/IMG_5309.jpg?v=1768826851',
    headline: 'UNDERLINE',
    subheadline: 'YOUR PERSONALITY',
    description: 'Discover our collection of waterproof, tarnish-free jewellery designed to elevate your everyday elegance.',
    cta: 'Shop Collection',
    link: '/jewellery'
  },
  {
    id: 2,
    image: 'https://cdn.shopify.com/s/files/1/0971/0187/3536/files/IMG_5315.jpg?v=1768826499',
    headline: 'TIMELESS',
    subheadline: 'ELEGANCE',
    description: '18K gold plated stainless steel that lasts. Make a statement that endures.',
    cta: 'Explore Now',
    link: '/collections'
  },
  {
    id: 3,
    image: 'https://cdn.shopify.com/s/files/1/0971/0187/3536/files/IMG_5147.jpg?v=1768827025',
    headline: 'PREMIUM',
    subheadline: 'CRAFTSMANSHIP',
    description: 'Each piece is meticulously crafted for those who appreciate luxury without compromise.',
    cta: 'View All',
    link: '/jewellery'
  }
];

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 700);
  }, [currentSlide, isAnimating]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section data-testid="hero-slider" className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[8000ms]"
            style={{
              backgroundImage: `url(${slide.image})`,
              transform: index === currentSlide ? 'scale(1.05)' : 'scale(1)'
            }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30 z-10" />

          {/* Content */}
          <div className="relative z-20 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
              <div className="max-w-2xl">
                {/* Headline */}
                <h1
                  className={`text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight leading-none mb-2 transition-all duration-700 delay-100 ${
                    index === currentSlide
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  }`}
                >
                  <span className="text-gold-gradient">{slide.headline}</span>
                </h1>
                <h2
                  className={`text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-none text-white mb-6 transition-all duration-700 delay-200 ${
                    index === currentSlide
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  }`}
                >
                  {slide.subheadline}
                </h2>

                {/* Gold Divider */}
                <div
                  className={`gold-divider-left mb-6 transition-all duration-700 delay-300 ${
                    index === currentSlide
                      ? 'opacity-100 w-20'
                      : 'opacity-0 w-0'
                  }`}
                />

                {/* Description */}
                <p
                  className={`text-white/70 text-lg md:text-xl max-w-lg mb-8 transition-all duration-700 delay-400 ${
                    index === currentSlide
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  }`}
                >
                  {slide.description}
                </p>

                {/* CTA Button */}
                <Link
                  to={slide.link}
                  data-testid={`hero-cta-${slide.id}`}
                  className={`btn-luxury inline-block transition-all duration-700 delay-500 ${
                    index === currentSlide
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  }`}
                >
                  {slide.cta}
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        data-testid="hero-prev"
        className="absolute left-6 top-1/2 -translate-y-1/2 z-30 p-3 border border-white/20 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
      </button>
      <button
        onClick={nextSlide}
        data-testid="hero-next"
        className="absolute right-6 top-1/2 -translate-y-1/2 z-30 p-3 border border-white/20 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
      </button>

      {/* Slider Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex space-x-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            data-testid={`hero-dot-${index}`}
            className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
